import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useStore } from '@/store'
import { getRegionLabel, getSevColor } from '@/constants'
import { authFetch } from '@/auth/authFetch'
import { API_BASE_URL } from '@/analysis/endpoints'
import type { Incident } from '@/types'

type StreamGroup = {
  streamId: string
  count: number
  openCount: number
  latestOpenedAt: string
  incidents: Incident[]
}

function normalizeIncident(raw: unknown): Incident | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const id = o.id != null ? String(o.id) : ''
  if (!id) return null
  const sev = o.sev === 0 || o.sev === 1 || o.sev === 2 ? o.sev : 0
  const status =
    o.status === 'investigating' || o.status === 'resolved' || o.status === 'open'
      ? o.status
      : 'open'
  const sliceType =
    typeof o.sliceType === 'string'
      ? o.sliceType
      : typeof o.pop === 'string'
        ? o.pop
        : undefined
  const sliceValue =
    typeof o.sliceValue === 'string'
      ? o.sliceValue
      : typeof o.con === 'string'
        ? o.con
        : undefined
  return {
    id,
    sid: typeof o.sid === 'string' ? o.sid : '',
    pop: typeof o.pop === 'string' ? o.pop : sliceType ?? '',
    con: typeof o.con === 'string' ? o.con : sliceValue ?? '',
    sliceType,
    sliceValue,
    sev,
    metric: typeof o.metric === 'string' ? o.metric : 'composite',
    val: typeof o.val === 'number' && Number.isFinite(o.val) ? o.val : 0,
    status,
    openedAt: typeof o.openedAt === 'string' ? o.openedAt : new Date().toISOString(),
  }
}

const SLICE_CHIPS = ['all', 'stream', 'country', 'studio', 'game', 'cdn_edge'] as const

async function patchStatus(id: string, status: string) {
  const res = await authFetch(`${API_BASE_URL}/api/incidents/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json() as Promise<{ incident?: Incident }>
}

export default function IncidentsView() {
  const incidents = useStore((s) => s.incidents)
  const syncToIncident = useStore((s) => s.syncToIncident)
  const setIncidents = useStore((s) => s.setIncidents)
  const setView = useStore((s) => s.setView)
  const drillToStream = useStore((s) => s.drillToStream)
  const descendToStreams = useStore((s) => s.descendToStreams)
  const setFilterOnly = useStore((s) => s.setFilterOnly)
  const setClientGeoFilter = useStore((s) => s.setClientGeoFilter)

  const [groupMode, setGroupMode] = useState<'flat' | 'stream'>('stream')
  const [sliceFilter, setSliceFilter] = useState<(typeof SLICE_CHIPS)[number]>('all')
  const [groups, setGroups] = useState<StreamGroup[]>([])
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [visible, setVisible] = useState(60)
  const [loading, setLoading] = useState(false)
  const [manualStream, setManualStream] = useState('')
  const [busyId, setBusyId] = useState<string | null>(null)
  const scRef = useRef<HTMLDivElement | null>(null)

  const reload = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (groupMode === 'stream') params.set('group', 'stream')
      if (sliceFilter !== 'all') params.set('sliceType', sliceFilter)
      const q = params.toString() ? `?${params}` : ''
      const res = await authFetch(`${API_BASE_URL}/api/incidents${q}`)
      if (!res.ok) return
      const body = await res.json() as { incidents?: unknown[]; groups?: StreamGroup[] }
      const parsed = (body.incidents ?? [])
        .map(normalizeIncident)
        .filter((x): x is Incident => x != null)
      setIncidents(parsed)
      if (body.groups) setGroups(body.groups)
      else setGroups([])
    } catch {
      /* WS may still deliver */
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupMode, sliceFilter, setIncidents])

  const openHexForSlice = (i: Incident) => {
    const sliceType = i.sliceType || i.pop || 'stream'
    const sliceValue = i.sliceValue || i.con || i.sid
    if (sliceType === 'studio' || sliceType === 'game' || sliceType === 'brand') {
      descendToStreams(sliceType, sliceValue)
      return
    }
    if (sliceType === 'continent') {
      setClientGeoFilter({ grain: 'continent', continent: sliceValue })
      setView('hex')
      return
    }
    if (sliceType === 'country' || sliceType === 'region') {
      setClientGeoFilter({ grain: 'country', country: sliceValue })
      setView('hex')
      return
    }
    if (sliceType === 'city') {
      setClientGeoFilter({ grain: 'city', city: sliceValue })
      setView('hex')
      return
    }
    if (sliceType === 'cdn_edge') {
      setFilterOnly('cdnEdge', sliceValue)
      setView('hex')
      return
    }
    if (i.sid) {
      drillToStream(i.sid)
    }
  }

  const sorted = useMemo(() => {
    return [...incidents].sort((a, b) => (a.openedAt < b.openedAt ? 1 : -1))
  }, [incidents])

  const onScroll = () => {
    const el = scRef.current
    if (!el) return
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 120
    if (nearBottom) setVisible((v) => Math.min(sorted.length, v + 60))
  }

  const act = async (id: string, status: string) => {
    setBusyId(id)
    try {
      await patchStatus(id, status)
      await reload()
    } catch {
      /* ignore */
    } finally {
      setBusyId(null)
    }
  }

  const flagManual = async () => {
    const streamId = manualStream.trim()
    if (!streamId) return
    setBusyId('manual')
    try {
      await authFetch(`${API_BASE_URL}/api/incidents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ streamId, severity: 'critical', metric: 'manual' }),
      })
      setManualStream('')
      await reload()
    } catch {
      /* ignore */
    } finally {
      setBusyId(null)
    }
  }

  const openDeep = (sid: string) => {
    if (!sid) return
    drillToStream(sid)
    setView('deep')
  }

  const renderActions = (i: Incident) => (
    <td style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      <span className="chip" style={{ fontSize: 10 }} onClick={() => syncToIncident(i.id, i.openedAt, i.sid)}>
        View
      </span>
      {(i.sliceType || i.pop) && (i.sliceType || i.pop) !== 'stream' && (
        <span className="chip" style={{ fontSize: 10 }} onClick={() => openHexForSlice(i)}>
          Hex
        </span>
      )}
      {i.sid && (
        <span className="chip" style={{ fontSize: 10 }} onClick={() => openDeep(i.sid)}>
          Deep
        </span>
      )}
      {i.status === 'open' && (
        <span
          className="chip"
          style={{ fontSize: 10, opacity: busyId === i.id ? 0.5 : 1 }}
          onClick={() => void act(i.id, 'investigating')}
        >
          Investigate
        </span>
      )}
      {(i.status === 'open' || i.status === 'investigating') && (
        <span
          className="chip"
          style={{ fontSize: 10, opacity: busyId === i.id ? 0.5 : 1 }}
          onClick={() => void act(i.id, 'resolved')}
        >
          Resolve
        </span>
      )}
    </td>
  )

  return (
    <div className="panel" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div className="panel-head" style={{ gap: 8, flexWrap: 'wrap' }}>
        <span className="panel-title">Incidents</span>
        <div style={{ display: 'flex', gap: 4, marginLeft: 8, flexWrap: 'wrap' }}>
          {SLICE_CHIPS.map((c) => (
            <button
              key={c}
              type="button"
              className="chip"
              onClick={() => setSliceFilter(c)}
              style={{
                fontSize: 10,
                fontWeight: sliceFilter === c ? 700 : 500,
                borderColor: sliceFilter === c ? 'var(--info-bd)' : undefined,
                background: sliceFilter === c ? 'var(--info-bg)' : undefined,
              }}
            >
              {c}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 4, marginLeft: 8 }}>
          {(['stream', 'flat'] as const).map((m) => (
            <span
              key={m}
              className={`chip${groupMode === m ? ' on' : ''}`}
              style={{ fontSize: 10 }}
              onClick={() => setGroupMode(m)}
            >
              {m === 'stream' ? 'By stream' : 'Timeline'}
            </span>
          ))}
        </div>
        <span style={{ fontSize: 10, color: 'var(--t-2)', marginLeft: 'auto' }}>
          {loading ? '…' : sorted.length}
        </span>
      </div>

      <div style={{ padding: '8px 12px', borderBottom: '0.5px solid var(--bd-0)', display: 'flex', gap: 8 }}>
        <input
          value={manualStream}
          onChange={(e) => setManualStream(e.target.value)}
          placeholder="Flag stream id…"
          style={{
            flex: 1, padding: '6px 8px', fontSize: 12,
            background: 'var(--bg-2)', border: '0.5px solid var(--bd-0)', borderRadius: 'var(--radius)',
            color: 'var(--t-0)',
          }}
        />
        <button
          type="button"
          className="chip"
          disabled={!manualStream.trim() || busyId === 'manual'}
          onClick={() => void flagManual()}
          style={{ fontSize: 11, cursor: 'pointer' }}
        >
          Flag
        </button>
      </div>

      <div ref={scRef} onScroll={onScroll} style={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
        {groupMode === 'stream' ? (
          <table className="vms-table">
            <thead>
              <tr>
                <th style={{ width: 24 }} />
                <th>Stream</th>
                <th style={{ width: 70 }}>Open</th>
                <th style={{ width: 70 }}>Total</th>
                <th style={{ width: 170 }}>Latest</th>
                <th style={{ width: 90 }}>Link</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => {
                const isOpen = expanded.has(g.streamId)
                return (
                  <Fragment key={g.streamId}>
                    <tr>
                      <td
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setExpanded((prev) => {
                            const next = new Set(prev)
                            if (next.has(g.streamId)) next.delete(g.streamId)
                            else next.add(g.streamId)
                            return next
                          })
                        }}
                      >
                        {isOpen ? '▾' : '▸'}
                      </td>
                      <td style={{ fontWeight: 500 }}>{g.streamId}</td>
                      <td style={{ color: g.openCount ? 'var(--crit)' : 'var(--t-2)' }}>{g.openCount}</td>
                      <td>{g.count}</td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{g.latestOpenedAt}</td>
                      <td>
                        <span className="chip" style={{ fontSize: 10 }} onClick={() => openDeep(g.streamId)}>
                          Deep dive
                        </span>
                      </td>
                    </tr>
                    {isOpen &&
                      g.incidents.map((i) => (
                        <tr key={i.id} style={{ background: 'var(--bg-2)' }}>
                          <td>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: getSevColor(i.sev), display: 'inline-block' }} />
                          </td>
                          <td colSpan={2} style={{ color: 'var(--t-1)' }}>
                            {i.metric} · {i.status}
                            {(i.sliceType || i.pop) && (i.sliceType || i.pop) !== 'stream' && (
                              <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--t-2)' }}>
                                {(i.sliceType || i.pop)}={i.sliceValue || i.con}
                              </span>
                            )}
                          </td>
                          <td style={{ color: 'var(--t-1)' }}>{Number(i.val).toLocaleString()}</td>
                          <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{i.openedAt}</td>
                          {renderActions(i)}
                        </tr>
                      ))}
                  </Fragment>
                )
              })}
              {groups.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: 18, textAlign: 'center', color: 'var(--t-2)' }}>
                    No incidents
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        ) : (
          <table className="vms-table">
            <thead>
              <tr>
                <th style={{ width: 18 }} />
                <th style={{ width: 90 }}>Status</th>
                <th style={{ width: 80 }}>Stream</th>
                <th style={{ width: 90 }}>Slice</th>
                <th style={{ width: 110 }}>Value</th>
                <th style={{ width: 70 }}>Metric</th>
                <th style={{ width: 70 }}>Value</th>
                <th style={{ width: 170 }}>Opened</th>
                <th style={{ width: 160 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {sorted.slice(0, visible).map((i) => (
                <tr key={i.id}>
                  <td>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: getSevColor(i.sev), display: 'inline-block' }} />
                  </td>
                  <td style={{ color: i.status === 'open' ? 'var(--crit)' : 'var(--t-2)', fontWeight: 500 }}>{i.status}</td>
                  <td style={{ fontWeight: 500, color: 'var(--t-0)' }}>{i.sid}</td>
                  <td style={{ color: 'var(--t-2)' }}>{i.pop}</td>
                  <td style={{ color: 'var(--t-2)' }}>{getRegionLabel(i.con)}</td>
                  <td style={{ color: 'var(--t-1)' }}>{i.metric}</td>
                  <td style={{ color: 'var(--t-1)' }}>{Number(i.val).toLocaleString()}</td>
                  <td style={{ color: 'var(--t-2)', fontFamily: 'var(--font-mono)' }}>{i.openedAt}</td>
                  {renderActions(i)}
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ padding: 18, textAlign: 'center', color: 'var(--t-2)' }}>
                    No incidents
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
